import React, { useState, useEffect } from "react";
import { Camera, Image as ImageIcon, ChevronLeft, ChevronRight, Layers, ZoomIn, ZoomOut, Maximize2, RotateCcw, RotateCw, X } from "lucide-react";
import photoService from "../../services/photoService";

const Phototheque = ({ contracts }) => {
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (contracts && contracts.length > 0 && !selectedContract) {
      const firstWithPhoto = contracts.find(
        (c) => c.jewelPhotos && c.jewelPhotos.length > 0
      );
      setSelectedContract(firstWithPhoto || contracts[0]);
    } else if (contracts && contracts.length > 0 && selectedContract) {
      // Ensure selected contract is still in the list
      const exists = contracts.find(c => c._id === selectedContract._id);
      if (!exists) {
        const firstWithPhoto = contracts.find(
          (c) => c.jewelPhotos && c.jewelPhotos.length > 0
        );
        setSelectedContract(firstWithPhoto || contracts[0]);
        setSelectedPhotoIndex(0);
      }
    }
  }, [contracts, selectedContract]);

  const handleContractClick = (contract) => {
    setSelectedContract(contract);
    setSelectedPhotoIndex(0);
    setZoom(100); // Reset zoom on change
  };

  const handleContractDoubleClick = (contract) => {
    if (selectedContract?._id !== contract._id) {
      setSelectedContract(contract);
      setSelectedPhotoIndex(0);
      setZoom(100);
    } else if (contract.jewelPhotos && contract.jewelPhotos.length > 1) {
      // Cycle to the next photo
      setSelectedPhotoIndex((prev) => (prev + 1) % contract.jewelPhotos.length);
      setZoom(100);
    }
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 400));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 25));

  // Safe getters for selected contract details
  const currentPhotos = selectedContract?.jewelPhotos || [];
  const currentPhoto = currentPhotos[selectedPhotoIndex];

  useEffect(() => {
    if (currentPhoto) {
      setRotation(currentPhoto.rotation || 0);
    }
  }, [currentPhoto]);

  const handleRotate = async (direction) => {
    if (!currentPhoto || !selectedContract) return;
    const newRotation = (rotation + direction) % 360;
    setRotation(newRotation);
    
    try {
      await photoService.updatePhotoDetails(selectedContract._id, currentPhoto._id, { rotation: newRotation });
      currentPhoto.rotation = newRotation; // Update in memory so it persists without reloading
      // Triggering a re-render can be done differently if needed, but since we removed updateTrigger, we rely on parent update or just let it be.
    } catch (error) {
      console.error("Erreur de sauvegarde de la rotation", error);
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(date);
    } catch (e) {
      return dateString;
    }
  };

  if (!contracts || contracts.length === 0) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh] text-gray-500">
        <p>Aucun contrat disponible pour la photothèque avec les filtres actuels.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-gray-50 overflow-hidden border rounded-lg shadow-inner">
      
      {/* LEFT PANEL: Thumbnail Grid (Previously Right) */}
      <div className="w-1/3 md:w-5/12 bg-gray-800 p-4 flex flex-col">
        <h2 className="text-white text-lg font-semibold mb-4 flex items-center shrink-0">
          <ImageIcon className="w-5 h-5 mr-2" />
          Tous les contrats ({contracts.length})
        </h2>
        
        <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 pb-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4 gap-4 auto-rows-max items-start">
            {contracts.map((contract) => {
              const hasPhoto = contract.jewelPhotos && contract.jewelPhotos.length > 0;
              const primaryPhoto = hasPhoto 
                ? contract.jewelPhotos.find(p => p.isPrimary) || contract.jewelPhotos[0] 
                : null;
              const isSelected = selectedContract?._id === contract._id;

              return (
                <div
                  key={contract._id}
                  onClick={() => handleContractClick(contract)}
                  onDoubleClick={() => handleContractDoubleClick(contract)}
                  className={`relative cursor-pointer rounded-xl overflow-hidden bg-[#2c333f] border border-gray-700/50 aspect-[3/4] flex flex-col transition-all duration-200 ${
                    isSelected 
                      ? "ring-4 ring-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.5)] scale-[1.02] z-10" 
                      : "hover:ring-2 hover:ring-gray-500 opacity-90 hover:opacity-100"
                  }`}
                  title={`Contrat ${contract.contractNumber || contract.dossier}`}
                >
                  <div className="flex-1 flex items-center justify-center p-2 pb-10 overflow-hidden">
                    {hasPhoto && primaryPhoto ? (
                      <img
                        src={primaryPhoto.url}
                        alt={`Contrat ${contract.contractNumber}`}
                        className="w-full h-full object-contain"
                        style={{ transform: `rotate(${primaryPhoto.rotation || 0}deg)` }}
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex flex-col items-center text-gray-500">
                        <Camera className="w-10 h-10 mb-2 opacity-50" />
                      </div>
                    )}
                  </div>

                {/* Multi-photo badge */}
                {contract.jewelPhotos?.length > 1 && (
                  <div className="absolute top-2 right-2 bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-md flex items-center">
                    <Layers className="w-3 h-3 mr-1" />
                    {contract.jewelPhotos.length}
                  </div>
                )}
                
                {/* Contract number overlay at bottom */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#1e293b] via-[#1e293b]/90 to-transparent p-3 pt-12">
                  <p className="text-gray-100 text-sm font-bold truncate text-center drop-shadow-md">
                    {contract.contractNumber || contract.dossier}
                  </p>
                </div>
              </div>
            );
          })}
          </div>
        </div>
      </div>

      {/* RIGHT PANEL: Selected Contract Photo & Details (Previously Left) */}
      <div className="w-2/3 md:w-7/12 flex flex-col border-l border-gray-200 bg-white">
        {selectedContract ? (
          <>
            {/* Header / Info */}
            <div className="flex flex-wrap items-center justify-between p-4 border-b border-gray-200 bg-white shadow-sm shrink-0">
              <div className="flex items-center space-x-2 text-sm">
                <span className="text-gray-500 font-medium">Numéro de contrat:</span>
                <span className="text-blue-700 font-bold">
                  {selectedContract.contractNumber || selectedContract.dossier || "N/A"}
                </span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <span className="text-gray-500 font-medium">Date création:</span>
                <span className="text-gray-800">
                  {formatDate(selectedContract.createdAt || selectedContract.date || selectedContract.dateCreation)}
                </span>
              </div>
              <div className="flex items-center space-x-2 text-sm bg-green-50 px-3 py-1 rounded">
                <span className="text-green-700 font-medium">Client:</span>
                <span className="text-green-800 font-bold uppercase">
                  {selectedContract.client || "INCONNU"}
                </span>
              </div>
            </div>

            {/* Main Image Area with Zoom */}
            <div className="flex-1 p-6 flex flex-col items-center justify-center bg-gray-100 overflow-hidden relative">
              {currentPhoto ? (
                <>
                  <div className="relative w-full h-full flex items-center justify-center overflow-auto custom-scrollbar">
                    <img
                      src={currentPhoto.url}
                      alt={`Vue du contrat ${selectedContract.contractNumber}`}
                      style={{ transform: `scale(${zoom / 100}) rotate(${rotation}deg)`, transformOrigin: 'center center', transition: 'transform 0.2s ease-in-out' }}
                      className="max-w-full max-h-full object-contain shadow-lg"
                    />
                  </div>
                  
                  {/* Tools Overlay: Zoom & Navigation */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center space-x-6 bg-white/90 px-6 py-2 rounded-full shadow-lg backdrop-blur-sm border border-gray-200">
                    
                    {/* Zoom controls */}
                    <div className="flex items-center space-x-3 border-r border-gray-300 pr-6">
                      <button onClick={handleZoomOut} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors text-gray-600" title="Dézoomer">
                        <ZoomOut className="w-5 h-5" />
                      </button>
                      <span className="w-12 text-center text-sm font-medium">{zoom}%</span>
                      <button 
                        onClick={handleZoomIn}
                        className="p-1.5 rounded-full hover:bg-gray-100 text-gray-700"
                        title="Zoom avant"
                      >
                        <ZoomIn className="w-5 h-5" />
                      </button>
                      
                      <div className="w-px h-6 bg-gray-300 mx-1"></div>
                      
                      <button 
                        onClick={() => handleRotate(-90)}
                        className="p-1.5 rounded-full hover:bg-gray-100 text-gray-700"
                        title="Pivoter à gauche"
                      >
                        <RotateCcw className="w-5 h-5" />
                      </button>
                      <button 
                        onClick={() => handleRotate(90)}
                        className="p-1.5 rounded-full hover:bg-gray-100 text-gray-700"
                        title="Pivoter à droite"
                      >
                        <RotateCw className="w-5 h-5" />
                      </button>
                      <button onClick={() => setIsModalOpen(true)} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors text-gray-600 ml-1" title="Plein écran">
                        <Maximize2 className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Navigation controls (if multiple photos) */}
                    {currentPhotos.length > 1 ? (
                      <div className="flex items-center space-x-3">
                        <button 
                          onClick={() => setSelectedPhotoIndex(prev => prev === 0 ? currentPhotos.length - 1 : prev - 1)}
                          className="p-1.5 hover:bg-gray-100 rounded-full transition-colors text-gray-600"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <span className="text-sm font-medium text-gray-600">
                          {selectedPhotoIndex + 1} / {currentPhotos.length}
                        </span>
                        <button 
                          onClick={() => setSelectedPhotoIndex(prev => prev === currentPhotos.length - 1 ? 0 : prev + 1)}
                          className="p-1.5 hover:bg-gray-100 rounded-full transition-colors text-gray-600"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-400 font-medium italic">Photo unique</div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center text-gray-400">
                  <ImageIcon className="w-16 h-16 mb-4 opacity-50" />
                  <p>Sélectionnez un contrat pour voir sa photo</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <p>Sélectionnez un contrat dans la liste</p>
          </div>
        )}
      </div>

      {/* Lightbox Modal (Plein écran) */}
      {isModalOpen && currentPhoto && (
        <div className="fixed inset-0 z-[9999] bg-black/95 flex flex-col">
          <div className="flex justify-end p-4 absolute top-0 right-0 z-50">
            <button 
              onClick={() => setIsModalOpen(false)}
              className="text-white hover:bg-white/20 p-2 rounded-full transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center overflow-hidden p-8">
            <img
              src={currentPhoto.url}
              alt="Plein écran"
              style={{ transform: `rotate(${rotation}deg)` }}
              className="max-w-full max-h-full object-contain"
            />
          </div>
          
          {/* Navigation in Modal */}
          {currentPhotos.length > 1 && (
            <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between px-8 pointer-events-none">
              <button 
                onClick={() => setSelectedPhotoIndex(prev => prev === 0 ? currentPhotos.length - 1 : prev - 1)}
                className="pointer-events-auto p-3 hover:bg-white/20 rounded-full transition-colors text-white"
              >
                <ChevronLeft className="w-10 h-10" />
              </button>
              <button 
                onClick={() => setSelectedPhotoIndex(prev => prev === currentPhotos.length - 1 ? 0 : prev + 1)}
                className="pointer-events-auto p-3 hover:bg-white/20 rounded-full transition-colors text-white"
              >
                <ChevronRight className="w-10 h-10" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Phototheque;
